import * as Entities from './entities';
/**
 * Pins Statuses
 *
 * Pins uses various statuses accross many of it's entities which are often
 * subsets of a large set. For example, an Insured can be 'archived' as well as
 * an insurance requirement, insurance policy, and job. To minimize duplicate constants
 * we have outlined each group of statuses below.
 *
 */

/**
 * These are the common statuses
 */
export const Statuses = {
  Approved: 'approved',
  Created: 'created',
  Archived: 'archived',
  Active: 'active',
  Canceled: 'canceled',
  Compliant: 'compliant',
  Deleted: 'deleted',
  Expired: 'expired',
  NonCompliant: 'non_compliant',
  NotApproved: 'not_approved',
  NotReceived: 'not_received',
  NotRequired: 'not_required',
  Pending: 'pending',
  Received: 'received',
  Waived: 'waived',
  Covered: 'covered',
  Uploading: 'uploading',
  Updating: 'updating',
  Success: 'success',
  Error: 'error',
  General: 'general',
  Required: 'required',
  UploadedElsewhere: 'uploaded_elsewhere'
};

/**
 * This is a mapping between statuses and their display names
 */
export const StatusDisplayNames = {
  [Statuses.Approved]: 'Approved',
  [Statuses.NotApproved]: 'Not Approved',
  [Statuses.Archived]: 'Archived',
  [Statuses.Active]: 'Active',
  [Statuses.Compliant]: 'Compliant',
  [Statuses.NonCompliant]: 'Non Compliant',
  [Statuses.Pending]: 'Pending',
  [Statuses.Deleted]: 'Deleted',
  [Statuses.Expired]: 'Expired',
  [Statuses.Waived]: 'Waived',
  [Statuses.NotReceived]: 'Not Received',
  [Statuses.Required]: 'Not Received'
};

/**
 * These are the statuses for each entity that can be selected manually by the user,
 * hence they will be shown in the status buttons and search filters
 */
const UserSelectedStatuses = {
  [Entities.InsuranceRequirement]: [
    Statuses.Waived,
    Statuses.Compliant,
    Statuses.NonCompliant,
    Statuses.Pending,
    Statuses.Archived,
    Statuses.Deleted
  ],
  [Entities.InsurancePolicy]: [
    Statuses.Active,
    Statuses.Archived,
    Statuses.Deleted
  ],
  [Entities.Job]: [
    Statuses.Archived,
    Statuses.Deleted
  ],
  [Entities.Insured]: [],
  [Entities.InsuranceRequirementDocument]: [
    Statuses.Pending,
    Statuses.Compliant,
    Statuses.NonCompliant,
    Statuses.Waived
  ],
  [Entities.InsuranceDocument]: [
    Statuses.Archived,
    Statuses.Deleted
  ]
};

/**
 * These are the statuses for each entity that are only set by the system
 * hence they will be shown in search filters, but not in the status buttons
 */
const SystemStatuses = {
  [Entities.InsuranceRequirement]: [],
  [Entities.InsurancePolicy]: [
    Statuses.Expired
  ],
  [Entities.Job]: [
    Statuses.Active
  ],
  [Entities.Insured]: [
    Statuses.Approved,
    Statuses.NotApproved,
    Statuses.Archived
  ],
  [Entities.InsuranceRequirementDocument]: [
    Statuses.Received,
    Statuses.NotReceived
  ],
  [Entities.InsuranceDocument]: [
    Statuses.Created
  ],
  [Entities.Template]: [
    Statuses.Updating,
    Statuses.Success,
    Statuses.Error,
    Statuses.General
  ]
};

/**
 * This takes both lists of statuses and combines them for use in search filters
 */
export const EntityStatuses = {
  [Entities.InsuranceRequirement]: [
    ...UserSelectedStatuses[Entities.InsuranceRequirement],
    ...SystemStatuses[Entities.InsuranceRequirement]
  ],
  [Entities.InsurancePolicy]: [
    ...UserSelectedStatuses[Entities.InsurancePolicy],
    ...SystemStatuses[Entities.InsurancePolicy]
  ],
  [Entities.Job]: [
    ...UserSelectedStatuses[Entities.Job],
    ...SystemStatuses[Entities.Job]
  ],
  [Entities.Insured]: [
    ...UserSelectedStatuses[Entities.Insured],
    ...SystemStatuses[Entities.Insured]
  ],
  [Entities.InsuranceDocument]: [
    ...UserSelectedStatuses[Entities.InsuranceDocument],
    ...SystemStatuses[Entities.InsuranceDocument]
  ],
  [Entities.Template]: SystemStatuses[Entities.Template]
};

export const SafeStatuses = [Statuses.Compliant, Statuses.Waived, Statuses.UploadedElsewhere];
